import { country_enum } from '@predium/enums';
import { FieldPath, FieldValues } from 'react-hook-form';
import { isAlphanumericPostalCode } from '@/utils/formUtils';
import RHFNumberField from './RHFNumberField';
import RHFTextField from './RHFTextField';

type Props<TFieldValues extends FieldValues = FieldValues> = {
  name: FieldPath<TFieldValues>;
  label?: string;
  /** Country whose postal-code format decides which input variant to render. */
  country: country_enum | null | undefined;
  disabled?: boolean;
};

/**
 * Postal-code input that adapts to the country's format.
 *
 * - Alphanumeric countries (e.g. UK "BT4 1GW") render a plain text field so letters aren't
 *   stripped, with an uppercase display transform.
 * - Digits-only countries (DE, AT, PL, FR) keep the numeric field (leading zeros allowed).
 */
export default function RHFPostalCodeField<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  country,
  disabled,
}: Props<TFieldValues>) {
  if (isAlphanumericPostalCode(country)) {
    return (
      <RHFTextField<TFieldValues>
        name={name}
        label={label}
        disabled={disabled}
        inputProps={{ style: { textTransform: 'uppercase' } }}
      />
    );
  }

  return (
    <RHFNumberField<TFieldValues>
      name={name}
      label={label}
      disabled={disabled}
      allowDecimals={false}
      allowLeadingZeros
      valueAsText
    />
  );
}
